import { IFieldRestrictions, IFieldPermissions } from 'store/pages/types';

/**
 * Get field permissions from render_hints
 */
export const getFieldPermissions = (
  fieldRestrictions: IFieldRestrictions | undefined,
  fieldName: string
): IFieldPermissions | null => {
  if (!fieldRestrictions || !fieldRestrictions[fieldName]) {
    return null;
  }
  return fieldRestrictions[fieldName];
};

/**
 * Check if field can be seen
 */
export const canSeeField = (
  fieldRestrictions: IFieldRestrictions | undefined,
  fieldName: string
): boolean => {
  const permissions = getFieldPermissions(fieldRestrictions, fieldName);
  return permissions ? permissions.can_see : true; // default to true if no restrictions
};

/**
 * Check if field can be updated
 */
export const canUpdateField = (
  fieldRestrictions: IFieldRestrictions | undefined,
  fieldName: string
): boolean => {
  const permissions = getFieldPermissions(fieldRestrictions, fieldName);
  return permissions ? permissions.can_update : true;
};

/**
 * Check if field can be created
 */
export const canCreateField = (
  fieldRestrictions: IFieldRestrictions | undefined,
  fieldName: string
): boolean => {
  const permissions = getFieldPermissions(fieldRestrictions, fieldName);
  return permissions ? permissions.can_create : true;
};

/**
 * Check if field should be disabled based on mode (create/edit)
 */
export const isFieldDisabled = (
  fieldRestrictions: IFieldRestrictions | undefined,
  fieldName: string,
  isEditMode: boolean
): boolean => {
  const permissions = getFieldPermissions(fieldRestrictions, fieldName);

  if (!permissions) {
    return false; // No restrictions, not disabled
  }

  // In edit mode, check can_update
  if (isEditMode) {
    return !permissions.can_update;
  }

  // In create mode, check can_create
  return !permissions.can_create;
};

/**
 * Validate field based on permissions and return error message
 */
export const validateFieldPermissions = (
  fieldRestrictions: IFieldRestrictions | undefined,
  fieldName: string,
  value: any,
  isEditMode: boolean
): string | undefined => {
  const permissions = getFieldPermissions(fieldRestrictions, fieldName);

  if (!permissions) {
    return undefined; // No restrictions, validation passes
  }

  // Check if field can be seen
  if (!permissions.can_see) {
    return undefined; // If user can't see it, don't validate
  }

  // Check permissions based on mode
  if (isEditMode && !permissions.can_update && value) {
    return 'You do not have permission to update this field';
  }

  if (!isEditMode && !permissions.can_create && value) {
    return 'You do not have permission to set this field';
  }

  return undefined;
};
